import { getHeights } from '@trippath/shared';
import { useEffect, useMemo, useState } from 'react';

import type { MapLine } from './map';

/** At most so many points asked of the server at once, as it takes them. */
const MOST = 2000;

/** The ways drawn by their ground's height: by road, by rail, on foot — not over the sea. */
const ON_GROUND: readonly string[] = ['train', 'bus', 'car', 'foot'];

/** Each way's ground heights, asked once while the page is open; empty while being asked. */
const known = new Map<string, readonly (number | null)[]>();
/** The maps drawn by them, each told as a way's heights come: whichever asked for them. */
const told = new Set<() => void>();

/** A way known by its stop and its line's ends and length. */
function keyOf(line: MapLine): string {
  return `${line.stop ?? ''} ${String(line.path.length)} ${String(line.path[0])} ${String(line.path.at(-1))}`;
}

/**
 * The ground's height at each point of a line: asked for at most [`MOST`] of them, evenly
 * along it, each point between them given the nearest one's.
 */
export async function groundOf(path: readonly [number, number][]): Promise<(number | null)[]> {
  const step = Math.ceil(path.length / MOST);
  const asked = path.filter((_, at) => at % step === 0);
  const { heights } = await getHeights(asked);
  return path.map(
    (_, at) => heights[Math.min(heights.length - 1, Math.round(at / step))] ?? null,
  );
}

/**
 * The trip's ways on the ground, each with its ground's height at each point once it is known —
 * a drive over a pass, a train through the mountains — to be drawn by it.
 */
export function useGround(lines: MapLine[]): MapLine[] {
  // How many ways' heights have come: the lines drawn again only then.
  const [asked, setAsked] = useState(0);
  const wanted = useMemo(
    () =>
      lines.filter(
        (line) =>
          line.heights === undefined &&
          line.stop !== undefined &&
          ON_GROUND.includes(line.kind),
      ),
    [lines],
  );
  useEffect(() => {
    const came = () => {
      setAsked((times) => times + 1);
    };
    told.add(came);
    for (const line of wanted) {
      const key = keyOf(line);
      if (known.has(key)) continue;
      known.set(key, []);
      groundOf(line.path).then(
        (heights) => {
          known.set(key, heights);
          for (const tell of told) tell();
        },
        () => {
          // Asked again next time.
          known.delete(key);
        },
      );
    }
    return () => {
      told.delete(came);
    };
  }, [wanted]);
  return useMemo(
    () =>
      lines.map((line) => {
        const heights = known.get(keyOf(line));
        return heights?.some((height) => height !== null) ? { ...line, heights } : line;
      }),
    // Drawn again as each way's heights come.
    [lines, asked],
  );
}

/** Forgets the heights asked: for tests. */
export function forgetGround(): void {
  known.clear();
  told.clear();
}
